<template>
  <div class="compatibility-quick-view">
    <div
      v-if="compatibilityTags?.length"
      class="compatibility-quick-view__icons"
    >
      <MzaIcon
        v-if="isMzaCompatible"
        size="large"
        direction="right"
        class="compatibility-quick-view__mza-icon"
      />
      <MzaIcon
        v-if="isMshCompatible"
        size="large"
        direction="right"
        class="compatibility-quick-view__mza-icon"
        version="msh"
      />
    </div>

    <!-- On bundles we can only show if a charger is included-->
    <Charger
      v-if="showCharger && bundleProduct"
      class="compatibility-quick-view__charger"
      :charger-included="hasChargerIncluded || false"
    />
    <Charger
      v-else-if="showCharger && standardProduct?.commonCharger"
      class="compatibility-quick-view__charger"
      :common-charger="standardProduct.commonCharger"
    />
  </div>
</template>

<script lang="ts" setup>
import MzaIcon from '~/components/mza-icon.vue';
import { Charger } from '@i22team/td-longtail-vue-components';

export type CompatibilityQuickViewProps = {
  offer?: Offer;
};

const props = withDefaults(defineProps<CompatibilityQuickViewProps>(), {
  offer: undefined,
});

const getCommonTags = (bundledProducts: OfferBundleProduct[]) => {
  const appTags = ['mzapro', 'mzabasic', 'mshpro', 'mshfree'];

  return appTags.filter((tag) =>
    bundledProducts?.every((product) =>
      product?.tags?.some((productTag) => productTag?.toLowerCase() === tag)
    )
  );
};

const product = computed(() => props.offer?.product);
const bundleProduct = computed(() => getBundleProduct<OfferProduct>(product.value));
const standardProduct = computed(() => getStandardProduct<OfferProduct>(product.value));

const compatibilityTags = computed(() => {
  const bundledProducts = bundleProduct.value?.bundledProducts;
  if (bundledProducts) {
    return bundledProducts.length > 0 ? getCommonTags(bundledProducts) : [];
  }
  return product.value?.tags || [];
});

const isMzaCompatible = computed(() => {
  if (compatibilityTags.value?.length < 1) return false;
  const lowerCaseTags = compatibilityTags.value.map((tag) => tag?.toLowerCase());
  return lowerCaseTags.some((tag) => ['mzabasic', 'mzapro'].includes(tag || ''));
});

const isMshCompatible = computed(() => {
  if (compatibilityTags.value?.length < 1) return false;
  const lowerCaseTags = compatibilityTags.value.map((tag) => tag?.toLowerCase());
  return lowerCaseTags.some((tag) => ['mshfree', 'mshpro'].includes(tag || ''));
});

const showCharger = computed(() => {
  if (!product.value) return false;
  if (standardProduct.value?.commonCharger?.possible) return true;
  return bundleProduct.value?.bundledProducts?.find((prod) => prod?.commonCharger?.possible);
});

const hasChargerIncluded = computed(() => {
  if (!product.value) return false;
  if (standardProduct.value?.commonCharger?.included) return true;
  return !!bundleProduct.value?.bundledProducts?.find(
    (prod) => prod?.commonCharger?.included === true
  );
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.compatibility-quick-view {
  &__icons,
  &__compatible-apps-wrapper {
    display: flex;
    gap: $grid-base;
  }

  &__app-icon {
    width: 4 * $grid-base;
    height: 4 * $grid-base;
  }

  &__charger {
    font-size: 5em;
  }

  &__mza-icon {
    &:first-child {
      z-index: 2;
    }
  }
}
</style>
